import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=phone, 2=otp, 3=newpassword
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const requestOTP = async () => {
    if (!phone || phone.length !== 10) { toast.error('Enter a valid 10-digit number'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { phone });
      toast.success('OTP sent to your phone');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send OTP');
    } finally { setLoading(false); }
  };

  const verifyAndReset = async () => {
    if (!otp) { toast.error('Enter the OTP'); return; }
    if (!newPassword || newPassword.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { phone, otp, new_password: newPassword });
      toast.success('Password reset successfully! Please login.');
      navigate('/login', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex flex-col px-4 py-8" style={{ background: '#FFF8F0' }}>
      <button data-testid="forgot-back" onClick={() => step > 1 ? setStep(step - 1) : navigate('/login')} className="self-start mb-6">
        <ArrowLeft size={24} color="#1B4F8A" />
      </button>

      <div className="w-full max-w-md mx-auto">
        <div className="text-center mb-8">
          <span className="text-5xl block mb-2">🔐</span>
          <h1 className="font-fredoka text-2xl" style={{ color: '#1B4F8A' }}>
            {step === 1 ? 'Forgot Password?' : step === 2 ? 'Enter OTP' : 'New Password'}
          </h1>
          <p className="text-sm font-semibold mt-1" style={{ color: '#8B7355' }}>
            {step === 1 ? 'Enter your phone number to get an OTP' : step === 2 ? 'Enter the OTP sent to your phone' : 'Create a new password'}
          </p>
        </div>

        <div className="space-y-4">
          {step === 1 && (
            <>
              <div className="flex gap-2">
                <span className="bg-white border-2 rounded-2xl px-3 py-3 font-bold text-sm flex items-center" style={{ borderColor: '#E5DACE', color: '#1B4F8A' }}>+91</span>
                <input data-testid="forgot-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)}
                  placeholder="9876543210" maxLength={10}
                  className="flex-1 bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#1B4F8A] transition-all"
                  style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
              </div>
              <button data-testid="forgot-send-otp" onClick={requestOTP} disabled={loading}
                className="btn-press w-full py-4 rounded-full font-extrabold text-white text-base"
                style={{ background: '#FF6B35', boxShadow: '0 4px 0 #C04918', opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Sending...' : 'Send OTP'}
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <input data-testid="forgot-otp" type="text" value={otp} onChange={e => setOtp(e.target.value)}
                placeholder="Enter 6-digit OTP" maxLength={6}
                className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm text-center tracking-[0.5em] outline-none focus:border-[#FF6B35] transition-all"
                style={{ borderColor: '#E5DACE', color: '#1B4F8A', fontSize: '1.25rem' }} />
              <p className="text-xs text-center font-semibold" style={{ color: '#8B7355' }}>
                Use OTP: <span className="font-mono" style={{ color: '#FF6B35' }}>123456</span> (demo)
              </p>
              <input data-testid="forgot-new-password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                className="w-full bg-white border-2 rounded-2xl px-4 py-3 font-bold text-sm outline-none focus:border-[#1B4F8A] transition-all"
                style={{ borderColor: '#E5DACE', color: '#1B4F8A' }} />
              <button data-testid="forgot-reset-btn" onClick={verifyAndReset} disabled={loading}
                className="btn-press w-full py-4 rounded-full font-extrabold text-white text-base"
                style={{ background: '#FF6B35', boxShadow: '0 4px 0 #C04918', opacity: loading ? 0.7 : 1 }}>
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
